import { Injectable } from '@angular/core';
import { Action, EnrichedAction } from 'src/app/core/models/action.model';
import {
  ClaimsService,
  ClaimWithCase,
} from 'src/app/core/services/claims.service';
import { KycStateService } from 'src/app/core/services/kyc-state.service';
import { KycService } from 'src/app/core/services/kyc.service';
import { Kyc_Check } from 'src/app/core/models/kyc_check.model';
import { ManagementStepService } from './managementStep.service';
import { UserService } from 'src/app/core/services/user.service';
import { notesAllowProceed } from 'src/app/core/utils/kyc-postpone.util';
import { AgentClientsService } from 'src/app/core/services/agent-clients.service';
import { QuestionnaireService } from 'src/app/core/services/survey.service';

@Injectable({
  providedIn: 'root',
})
export class GuideService {
  mainClaim: ClaimWithCase | null = null;

  requirementsFormClaim: Action[] | null = null;

  // Claim a que `requirementsFormClaim` pertence — sem isso, o cache era um
  // singleton "global" e o guide de qualquer claim que não fosse a mainClaim
  // acabava mostrando as actions da claim errada (CPK-3911).
  requirementsFormClaimId: string | null = null;

  private skipCheck = false;

  constructor(
    private claimsService: ClaimsService,
    private kycStateService: KycStateService,
    private kycService: KycService,
    private managementStepService: ManagementStepService,
    private userService: UserService,
    private agentClientsService: AgentClientsService,
    private questionnaireService: QuestionnaireService
  ) {}

  async getMainClaim(forceRefresh: boolean = false) {
    if (forceRefresh || !this.mainClaim) {
      this.mainClaim = (
        await this.claimsService.getMyClaimsWithCase(1, 0)
      )?.[0];
    }
    return this.mainClaim;
  }

  /**
   * Força refresh da main claim - útil quando o status pode ter mudado
   */
  async refreshMainClaim(): Promise<ClaimWithCase | null> {
    console.log('[GUIDE_SERVICE] Refreshing main claim');
    this.mainClaim = null;
    this.requirementsFormClaim = null; // Também limpa requirements
    this.requirementsFormClaimId = null;
    return await this.getMainClaim(true);
  }

  /**
   * Verifica APENAS se o wizard está habilitado para o case
   * NÃO carrega actions - usado pelo guard para decisão rápida
   */
  async isWizardEnabled(): Promise<boolean> {
    const mainClaim = await this.getMainClaim();

    if (!mainClaim) {
      return false;
    }

    return !!mainClaim.cp_case?.configuration?.enable_actions_wizard;
  }

  async checkIfUserMustAccessGuide(
    mode: 'any' | 'all' = 'all',
    claimId?: string
  ): Promise<boolean> {
    console.log('[GUIDE_SERVICE] Checking if user must access guide', { mode, claimId });

    // ✅ Quando um claimId específico é passado (multi-claim), avalia essa
    // claim em vez da mainClaim cacheada — sem isso, o guide de qualquer
    // claim que não fosse a primeira carregada na sessão acabava mostrando
    // as actions da claim errada (CPK-3911).
    const claim = claimId
      ? await this.claimsService.getClaimWithCase(claimId)
      : await this.getMainClaim();

    console.log('[GUIDE_SERVICE] Current target claim', {
      skipCheck: this.skipCheck,
      claim,
    })
    if (this.skipCheck || !claim) {
      console.log('[GUIDE_SERVICE] Skip check or no claim', {
        skipCheck: this.skipCheck,
        hasClaim: !!claim,
        mode,
      });
      return false;
    }

    // Só claims ACTIVE podem ser destino do guide — ClaimActiveGuard exige
    // status === 'ACTIVE' na rota /guide, então qualquer outro status
    // (INACTIVE, WITHDRAWN, etc.) resultaria num redirect para o guide
    // seguido de bounce imediato de volta para a Home.
    if (claim.status !== 'ACTIVE') {
      console.log('[GUIDE_SERVICE] Claim is not ACTIVE, guide not required', {
        claimId: claim.id,
        status: claim.status,
        mode,
      });
      return false;
    }

    const isWizardEnabled =
      !!claim.cp_case?.configuration?.enable_actions_wizard;
    if (!isWizardEnabled) {
      console.log('[GUIDE_SERVICE] Wizard not enabled for this case', {
        caseId: claim.cp_case?.id,
        mode,
      });
      return false;
    }

    // Invalida o cache de requirements se mudamos de claim
    if (this.requirementsFormClaimId !== claim.id) {
      this.requirementsFormClaim = null;
    }

    if (!this.requirementsFormClaim) {
      // ✅ Usa novo método V3 com ordenação topológica e suporte a múltiplos questionários
      const enrichedActions = await this.claimsService.getOutstandingActionsV3(
        claim.id
      );

      console.log('[GUIDE_SERVICE] Raw requirements from DB (V3)', {
        claimId: claim.id,
        totalEnrichedActions: enrichedActions?.length || 0,
        enrichedActions: enrichedActions?.map(r => ({
          caseActionId: r.case_action_id,
          actionId: r.action_id,
          type: r.action_type,
          status: r.ac_status,
          prstatus: r.prstatus,
          doneActionId: r.done_action_id
        }))
      });

      // ✅ Converte EnrichedAction[] para Action[] e deduplica KYCs
      const rawActions = this.convertEnrichedToAction(enrichedActions || []);
      const deduplicatedActions = await this.deduplicateActions(rawActions);
      this.requirementsFormClaim = await this.filterImpersonatedActions(deduplicatedActions);
      this.requirementsFormClaimId = claim.id;

      console.log('[GUIDE_SERVICE] Deduplicated requirements', {
        claimId: claim.id,
        totalRequirements: this.requirementsFormClaim?.length || 0,
        mode,
        requirements: this.requirementsFormClaim?.map(r => ({
          id: r.action_id,
          type: r.action_type,
          status: r.ac_status,
          doneActionId: r.done_action_id
        }))
      });
    }

    // Se não tem actions, não precisa acessar guide
    if (!this.requirementsFormClaim || this.requirementsFormClaim.length === 0) {
      console.log('[GUIDE_SERVICE] No requirements found', { mode });
      return false;
    }

    // ✅ Usa ManagementStepService para verificar status atualizado
    // Usa getState() em vez de getAllActionsState() para parar na primeira incompleta (otimização)
    const user = await this.userService.getCurrentUser();
    let allActionsComplete = false;

    if (user && user.id) {
      // Se mode === 'any', precisamos saber se PELO MENOS UMA está completa.
      // getState para na primeira incompleta.
      // Se a primeira for incompleta, getState retorna. Não sabemos sobre as outras.
      // Mas para o caso de uso do Guide, geralmente queremos saber se "tem algo pendente".

      if (mode === 'any') {
        // Para 'any', infelizmente precisamos verificar todas ou pelo menos até achar uma completa
        // Mas como o caso de uso principal é 'all' (bloqueio de navegação), vamos otimizar para 'all'
        const states = await this.managementStepService.getAllActionsState(
          this.requirementsFormClaim,
          claim.id,
          user.id
        );
        const completionChecks = states.map(s => ({ action: s.action, isComplete: s.isComplete }));
        allActionsComplete = completionChecks.some(check => check.isComplete);
      } else {
        // Modo 'all' (padrão): Queremos saber se TODAS estão completas.
        // getState() verifica sequencialmente e para na primeira incompleta.
        // Se retornar allActionsComplete = false, então nem todas estão completas -> mustAccess = true.
        const wizardState = await this.managementStepService.getState(
          this.requirementsFormClaim,
          claim.id,
          user.id
        );
        allActionsComplete = wizardState.allActionsComplete;
      }
    } else {
      console.warn('[GUIDE_SERVICE] No user found, cannot check action status');
      allActionsComplete = false;
    }

    if (mode === 'any') {
      console.log('[GUIDE_SERVICE] Check result (ANY mode)', {
        allActionsComplete,
        mustAccessGuide: !allActionsComplete,
      });
    } else {
      console.log('[GUIDE_SERVICE] Check result (ALL mode)', {
        allActionsComplete,
        mustAccessGuide: !allActionsComplete,
      });
    }

    // Deve acessar o guide se NEM TODAS (ou NENHUMA no modo 'any') estão completas
    return !allActionsComplete;
  }

  /**
   * Verifica se um claim ESPECÍFICO ainda exige acesso ao guide (wizard
   * habilitado e com actions pendentes), independente do mainClaim.
   * Usado pelo guard e pelos componentes de outstanding actions (oa-kyc,
   * oa-signature) para decidir se uma ação deve abrir o modal diretamente
   * ou redirecionar para o guide daquele claim (CPK-3911).
   */
  async claimRequiresGuide(claimId: string): Promise<boolean> {
    // Delega pra checkIfUserMustAccessGuide, que já faz exatamente essa
    // checagem (status ACTIVE, wizard habilitado, actions pendentes) e
    // popula o cache de requirements por claimId. Antes esta era uma
    // implementação duplicada que nunca populava esse cache — então, ao
    // redirecionar pro guide logo em seguida, o GuideComponent refazia toda
    // a mesma checagem cara do zero (getOutstandingActionsV3 + dedup +
    // managementStepService.getState), causando um delay perceptível no
    // clique do card antes do redirect.
    return this.checkIfUserMustAccessGuide('all', claimId);
  }

  /**
   * Verifica se uma action está completa baseado no tipo e status
   */
  private async isActionComplete(action: Action): Promise<boolean> {
    const status = action.ac_status;

    switch (action.action_type) {
      case 'KYC':
        // KYC precisa ter status DONE E passed = true
        // ✅ Após correção: ac_status só fica 'DONE' se passed=true
        // Se passed=false, ac_status fica como 'FAILED'
        if (status !== 'DONE') {
          return false;
        }

        // Verifica se realmente passou no KYC check
        if (action.done_action_id) {
          try {
            const kycCheck = await this.kycStateService.getKycCheck(action.done_action_id);

            if (!kycCheck) {
              console.warn('[GUIDE_SERVICE] KYC check not found', {
                actionId: action.action_id,
                doneActionId: action.done_action_id,
              });
              return false;
            }

            const passed = kycCheck.passed === true;

            // Check if KYC was bypassed by an agent (server-side flag)
            const currentUser = await this.userService.getCurrentUser();
            const bypassedByAgent = !!(currentUser?.attributes as any)?.kyc?.bypassedByAgent;

            // CPK-3620: FAILED but postponable (notes.postpone_reason.canProceed)
            const canProceed = notesAllowProceed((kycCheck as any).notes);

            console.log('[GUIDE_SERVICE] KYC check validated', {
              actionId: action.action_id,
              kycCheckId: action.done_action_id,
              passed,
              bypassedByAgent,
              canProceed,
            });

            return passed || bypassedByAgent || canProceed;
          } catch (error) {
            console.error('[GUIDE_SERVICE] Error validating KYC check', {
              actionId: action.action_id,
              error: error instanceof Error ? error.message : String(error),
            });
            return false;
          }
        }

        // Se não tem done_action_id mas status = DONE, considera incompleto
        console.warn('[GUIDE_SERVICE] KYC marked DONE but no kyc_check', {
          actionId: action.action_id,
        });
        return false;

      case 'SIGNATURE':
        // Assinatura só está completa se status = SIGNED
        return status === 'SIGNED';

      case 'QUESTIONNAIRE':
        // Questionário aceita DONE ou SUBMITTED
        return status === 'DONE' || status === 'SUBMITTED';

      default:
        // Para outros tipos, considera completo se tem status DONE
        return status === 'DONE';
    }
  }

  /**
   * Converte EnrichedAction[] para Action[]
   * Mantém compatibilidade com código existente
   */
  private convertEnrichedToAction(enrichedActions: EnrichedAction[]): Action[] {
    return enrichedActions.map(enriched => ({
      case_action_id: enriched.case_action_id,
      action_id: enriched.action_id,
      claim_id: enriched.claim_id,
      done_action_id: enriched.done_action_id,
      action_name: enriched.action_name,
      action_type: enriched.action_type,
      type: enriched.type,
      config: enriched.config,
      case_action_config: enriched.case_action_config,
      questionnaire_id: enriched.questionnaire_id,
      contract_id: enriched.contract_id,
      ac_status: enriched.ac_status,
      prstatus: enriched.prstatus,
      status: enriched.status,
      prerequisite_ids: enriched.prerequisite_ids || [],
      description: enriched.description,
      created_at: enriched.created_at,
    } as Action));
  }

  /**
   * Deduplica actions, especialmente KYC que pode ter múltiplas tentativas
   * ✅ Preserva a ordem topológica original (não move KYC pro final)
   */
  private async deduplicateActions(rawActions: Action[]): Promise<Action[]> {
    // Separa KYCs do resto
    const kycs = rawActions.filter((e) => e.action_type === 'KYC');
    const nonKycs = rawActions.filter((e) => e.action_type !== 'KYC');

    console.log('[GUIDE_SERVICE] Deduplicating actions', {
      totalRaw: rawActions.length,
      kycsCount: kycs.length,
      nonKycsCount: nonKycs.length,
    });

    // Para KYCs, seleciona o melhor (aprovado ou mais recente)
    const selectedKyc = await this.selectBestKyc(kycs);

    // Reconstrói a lista preservando a ordem topológica original
    // Percorre rawActions na ordem original, deduplicando:
    // - Non-KYC: usa case_action_id como chave de dedup
    // - KYC: substitui todas as ocorrências pelo selectedKyc (apenas na primeira posição de KYC)
    const seenCaseActionIds = new Set<string>();
    let kycInserted = false;
    const finalList: Action[] = [];

    for (const action of rawActions) {
      if (action.action_type === 'KYC') {
        // Insere o KYC selecionado apenas na posição do primeiro KYC encontrado
        if (!kycInserted && selectedKyc) {
          finalList.push(selectedKyc);
          kycInserted = true;
        }
        // Pula KYCs duplicados
        continue;
      }

      // Non-KYC: dedup por case_action_id
      const key = action.case_action_id || action.action_id;
      if (key && !seenCaseActionIds.has(key)) {
        seenCaseActionIds.add(key);
        finalList.push(action);
      }
    }

    // ✅ NÃO reordena por tipo — a ordem topológica do sortCaseActions() já respeita pré-requisitos

    console.log('🔎 [WIZARD_DEBUG] DEDUP_FINAL_ORDER', {
      finalCount: finalList.length,
      order: finalList.map((a, idx) => ({
        step: idx,
        name: a.action_name,
        type: a.action_type,
        caseActionId: a.case_action_id?.slice(0,8),
        actionId: a.action_id?.slice(0,8),
        prstatus: a.prstatus,
        prerequisite_ids: (a.prerequisite_ids || []).map(id => id.slice(0,8)),
        ac_status: a.ac_status,
      })),
    });

    return finalList;
  }

  /**
   * Filtra actions de QUESTIONNAIRE com agent_impersonate_skip quando
   * a sessão é uma impersonação de agente (JWT contém impersonated_by).
   */
  private async filterImpersonatedActions(actions: Action[]): Promise<Action[]> {
    if (!this.agentClientsService.isAgentSession()) {
      return actions;
    }

    // If agent is in skipGuide mode targeting a specific questionnaire, keep it
    const agentUploadTarget = localStorage.getItem('agentUploadQuestionnaireId');
    const skippedCaseActionIds = new Set<string>();

    console.log('[GUIDE_SERVICE] Agent session detected, filtering agent_impersonate_skip actions', {
      agentUploadTarget,
    });

    const filtered: Action[] = [];
    for (const action of actions) {
      if (action.action_type === 'QUESTIONNAIRE' && action.questionnaire_id) {
        // Never filter out the questionnaire the agent explicitly wants to access
        if (action.questionnaire_id === agentUploadTarget) {
          filtered.push(action);
          continue;
        }

        try {
          const questionnaire = await this.questionnaireService.get(action.questionnaire_id);
          if (questionnaire?.configuration?.agent_impersonate_skip) {
            console.log('[GUIDE_SERVICE] Skipping action (agent_impersonate_skip)', {
              actionId: action.action_id,
              questionnaireId: action.questionnaire_id,
            });
            if (action.case_action_id) {
              skippedCaseActionIds.add(action.case_action_id);
            }
            continue;
          }
        } catch (error) {
          console.warn('[GUIDE_SERVICE] Error fetching questionnaire config, keeping action', {
            questionnaireId: action.questionnaire_id,
            error: error instanceof Error ? error.message : String(error),
          });
        }
      }
      filtered.push(action);
    }

    // CPK-4035 (parte 2): uma action pulada por agent_impersonate_skip não pode
    // continuar bloqueando quem a listava como pré-requisito — senão "pular"
    // vira "travar" (checkPrerequisitesComplete trata pré-requisito ausente da
    // lista como não cumprido, não como não aplicável).
    if (skippedCaseActionIds.size > 0) {
      for (const action of filtered) {
        if (action.prerequisite_ids?.length) {
          action.prerequisite_ids = action.prerequisite_ids.filter(
            id => !skippedCaseActionIds.has(id)
          );
        }
      }
    }

    console.log('[GUIDE_SERVICE] Actions after impersonation filter', {
      before: actions.length,
      after: filtered.length,
      removed: actions.length - filtered.length,
      skippedCaseActionIds: Array.from(skippedCaseActionIds),
    });

    return filtered;
  }

  /**
   * Seleciona o melhor KYC de uma lista de tentativas
   * Prioriza: 1) KYC aprovado, 2) KYC mais recente
   */
  private async selectBestKyc(kycs: Action[]): Promise<Action | null> {
    if (kycs.length === 0) {
      return null;
    }

    if (kycs.length === 1) {
      return kycs[0];
    }

    console.log('[GUIDE_SERVICE] Selecting best KYC from multiple attempts', {
      totalAttempts: kycs.length,
    });

    // Busca os KYC checks de todas as tentativas
    const kycChecks: (Kyc_Check | null)[] = [];

    for (const kyc of kycs) {
      if (kyc.done_action_id) {
        try {
          const check = await this.kycService.getById(kyc.done_action_id);
          kycChecks.push(check);
        } catch {
          kycChecks.push(null);
        }
      } else {
        kycChecks.push(null);
      }
    }

    // 1) Procura por KYC aprovado (passed = true) ou adiável (postpone canProceed)
    for (let i = 0; i < kycChecks.length; i++) {
      if (kycChecks[i]?.passed === true || notesAllowProceed((kycChecks[i] as any)?.notes)) {
        console.log('[GUIDE_SERVICE] Found approved/postponed KYC', {
          index: i,
          actionId: kycs[i].action_id,
          kycCheckId: kycs[i].done_action_id,
        });
        return kycs[i];
      }
    }

    console.log('[GUIDE_SERVICE] No approved KYC found, selecting most recent');

    // 2) Nenhum aprovado - pega o mais recente
    const validChecks = kycChecks
      .map((check, index) => ({ check, index }))
      .filter(item => item.check !== null) as { check: Kyc_Check; index: number }[];

    if (validChecks.length === 0) {
      // Fallback: retorna o primeiro
      return kycs[0];
    }

    // Ordena por data de criação (mais recente primeiro)
    validChecks.sort((a, b) => {
      const dateA = new Date(a.check.created_at!).getTime();
      const dateB = new Date(b.check.created_at!).getTime();
      return dateB - dateA; // Mais recente primeiro
    });

    const selectedIndex = validChecks[0].index;

    console.log('[GUIDE_SERVICE] Selected most recent KYC', {
      index: selectedIndex,
      actionId: kycs[selectedIndex].action_id,
      kycCheckId: kycs[selectedIndex].done_action_id,
      createdAt: validChecks[0].check.created_at,
    });

    return kycs[selectedIndex];
  }

  resetService() {
    this.mainClaim = null;
    this.requirementsFormClaim = null;
    this.requirementsFormClaimId = null;
    this.skipCheck = false;
  }

  /**
   * Força um refresh das requirements do claim atual
   * Útil para verificar mudanças entre steps do wizard
   */
  async refreshRequirements(claimId?: string): Promise<void> {
    console.log('[GUIDE_SERVICE] Refreshing requirements');

    // Limpa cache
    this.requirementsFormClaim = null;
    this.requirementsFormClaimId = null;

    // Recarrega se houver main claim ou um claimId explícito
    if (claimId || this.mainClaim) {
      await this.checkIfUserMustAccessGuide('all', claimId);
    }
  }

  setSkipCheckTrue() {
    console.log('[GUIDE_SERVICE] Setting skipCheck to true');
    this.skipCheck = true;
  }
}
